"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  Typography,
  Select,
  Option,
  Radio,
  Button,
} from "@material-tailwind/react";
import { isEmpty } from "lodash";
import zipCodes from "../../../public/thai-address/zipcodes.json";
import provinces from "../../../public/thai-address/provinces.json";
import districts from "../../../public/thai-address/districts.json";
import subDistricts from "../../../public/thai-address/sub-districts.json";
import ArrowIcon from "@/components/ArrowIcon";
import memberApi from "@/api/member";
import {
  DialogMode,
  NotificationDialog,
  TNotificationDialog,
} from "@/components/NotificationDialog";
import RecommenderCard from "@/features/Home/components/RecommenderCard";
import {
  memberRegisterSchema,
  MemberRegistrationRequest,
} from "@/features/Member/member.types";
import fileApi from "@/api/file";
import Sign from "@/components/Sign";
// import ReCAPTCHA from "react-google-recaptcha";
import recommenderService from "@/features/Recommender/services";
import ProvinceSelect from "@/components/ProvinceSelect";
import RegisterMemberInfo from "@/features/Member/RegisterMemberInfo";
import UploadedFile from "@/features/Member/components/UploadFile";
import { tracker } from "@/helpers/openReplay";
import { ZodError } from "zod";
import imageCompression from "browser-image-compression";

type ValidInputs = Record<keyof MemberRegistrationRequest, boolean>;
type SearchPostalCodeResult = {
  subDistrictId: string;
  district: string;
  postalCode: string;
  province: string;
  subDistrict: string;
};

const requirements = [
  "เพื่อซื้อในราคาทุน",
  "ต้องการรายได้เสริม",
  "ต้องการทำธุรกิจส่วนตัว",
  "เพื่อซื้อในราคาทุน และต้องการรายได้เสริม",
];
const namePrefix = ["นาย", "นาง", "นางสาว", "อื่น ๆ"];
const maritalStatuses = ["โสด", "สมรส", "หย่าร้าง", "หม้าย"];
const prefixForSpouseNames = ["นาย", "นาง", "นางสาว"];
const bankAccountTypes = ["ไม่ระบุ", "ออมทรัพย์", "กระแสรายวัน"];
const banks: string[] = [
  "ธนาคารกรุงเทพ จำกัด (มหาชน)",
  "ธนาคารกรุงไทย จำกัด (มหาชน)",
  "ธนาคารกรุงศรีอยุธยา จำกัด (มหาชน)",
  "ธนาคารกสิกรไทย จำกัด (มหาชน)",
  "ธนาคารเกียรตินาคินภัทร จำกัด (มหาชน)",
  "ธนาคารซีไอเอ็มบี ไทย จำกัด (มหาชน)",
  "ธนาคารทหารไทยธนชาต จำกัด (มหาชน)",
  "ธนาคารทิสโก้ จำกัด (มหาชน)",
  "ธนาคารไทยเครดิต จำกัด (มหาชน)",
  "ธนาคารไทยพาณิชย์ จำกัด (มหาชน)",
  "ธนาคารยูโอบี จำกัด (มหาชน)",
  "ธนาคารแลนด์ แอนด์ เฮ้าส์ จำกัด (มหาชน)",
  "ธนาคารสแตนดาร์ดชาร์เตอร์ด (ไทย) จำกัด (มหาชน)",
  "ธนาคารไอซีบีซี (ไทย) จำกัด (มหาชน)",
  "ธนาคารแห่งประเทศจีน (ไทย) จำกัด (มหาชน)",
];
const occupations = [
  "พนักงานบริษัท",
  "ราชการ / รัฐวิสาหกิจ",
  "ธุรกิจส่วนตัว / ค้าขาย",
  "ตัวแทนประกันวินาศภัย",
  "ตัวแทนประกันชีวิต",
  "อื่น ๆ",
];

const defaultModel: MemberRegistrationRequest = {
  uplinerId: 1,
  requirementsForMembershipRegistration: "เพื่อซื้อในราคาทุน",
  email: "",
  lineId: "",
  prefixForName: "นาย",
  otherPrefixesForNames: "",
  firstName: "",
  lastName: "",
  nationalIdNumber: "",
  birthdate: null,
  birthMonth: null,
  birthYear: null,
  mobilePhone: "",
  homePhone: "",
  workPhone: "",
  workPhoneExtensionNumber: "",
  maritalStatus: "โสด",
  prefixForSpouseName: "",
  spouseFirstName: "",
  spouseLastName: "",
  nameOfTheLocation: "",
  roomOrFloor: "",
  addressNumber: "",
  villageNumber: "",
  soi: "",
  road: "",
  province: "",
  district: "",
  subDistrict: "",
  postalCode: "",
  bankAccountType: "ไม่ระบุ",
  bank: "",
  bankBranch: "",
  bankAccountName: "",
  bankAccountNumber: "",
  occupation: "พนักงานบริษัท",
  otherOccupations: "",
  paCompany: "",
  paPlan: "",
  hasANonLifeInsuranceBrokerLicense: false,
  nonLifeInsuranceBrokerLicenseNumber: null,
  nonLifeInsuranceBrokerLicenseExpiredDate: null,
  nonLifeInsuranceBrokerLicenseExpiredMonth: null,
  nonLifeInsuranceBrokerLicenseExpiredYear: null,
  hasANonLifeInsuranceBrokerLifeLicense: false,
  nonLifeInsuranceBrokerLifeLicenseNumber: null,
  nonLifeInsuranceBrokerLifeLicenseExpiredDate: null,
  nonLifeInsuranceBrokerLifeLicenseExpiredMonth: null,
  nonLifeInsuranceBrokerLifeLicenseExpiredYear: null,
  beneficiaryFirstName: "",
  beneficiaryLastName: "",
  beneficiaryLastNationalIdNumber: "",
  nationalCardFileGDriveId: "",
  nationalCardFileGDriveIdPub: "",
  bankAccountFileGDriveId: "",
  bankAccountFileGDriveIdPub: "",
  nonLifeInsuranceBrokerFileGDriveId: "",
  nonLifeInsuranceBrokerFileGDriveIdPub: "",
  nonLifeInsuranceBrokerLifeFileGDriveId: "",
  nonLifeInsuranceBrokerLifeFileGDriveIdPub: "",
  paymentSlipGDriveId: "",
  paymentSlipGDriveIdPub: "",
  signatureImageUrl: "",
};

const gDriveUrl = process.env.NEXT_PUBLIC_G_DRIVE_DISPLAY_FILE_URL;
const MemberRegister = () => {
  const [model, setModel] = useState<MemberRegistrationRequest>(defaultModel);

  const [validInputs, setValidInputs] = useState<Partial<ValidInputs>>({
    requirementsForMembershipRegistration: true,
    email: true,
    lineId: true,
    prefixForName: true,
    otherPrefixesForNames: true,
    firstName: true,
    lastName: true,
    nationalIdNumber: true,
    birthdate: true,
    birthMonth: true,
    birthYear: true,
    mobilePhone: true,
    homePhone: true,
    workPhone: true,
    workPhoneExtensionNumber: true,
    maritalStatus: true,
    prefixForSpouseName: true,
    spouseFirstName: true,
    spouseLastName: true,
    nameOfTheLocation: true,
    roomOrFloor: true,
    addressNumber: true,
    villageNumber: true,
    soi: true,
    road: true,
    province: true,
    district: true,
    subDistrict: true,
    postalCode: true,
    bankAccountType: true,
    bank: true,
    bankBranch: true,
    bankAccountName: true,
    bankAccountNumber: true,
    occupation: true,
    otherOccupations: true,
    paCompany: true,
    paPlan: true,
    hasANonLifeInsuranceBrokerLicense: true,
    nonLifeInsuranceBrokerLicenseNumber: true,
    nonLifeInsuranceBrokerLicenseExpiredDate: true,
    nonLifeInsuranceBrokerLicenseExpiredMonth: true,
    nonLifeInsuranceBrokerLicenseExpiredYear: true,
    hasANonLifeInsuranceBrokerLifeLicense: true,
    nonLifeInsuranceBrokerLifeLicenseNumber: true,
    nonLifeInsuranceBrokerLifeLicenseExpiredDate: true,
    nonLifeInsuranceBrokerLifeLicenseExpiredMonth: true,
    nonLifeInsuranceBrokerLifeLicenseExpiredYear: true,
    beneficiaryFirstName: true,
    beneficiaryLastName: true,
    beneficiaryLastNationalIdNumber: true,
    paymentSlipGDriveId: true,
    paymentSlipGDriveIdPub: true,
  });
  const [searchZipcode, setSearchZipcode] = useState<string>("");
  const requirementsForMembershipRegistrationRef =
    useRef<HTMLInputElement>(null);
  const birthdateRef = useRef<HTMLInputElement>(null);
  const birthMonthRef = useRef<HTMLInputElement>(null);
  const birthYearRef = useRef<HTMLInputElement>(null);
  const provinceRef = useRef<HTMLInputElement>(null);
  const districtRef = useRef<HTMLInputElement>(null);
  const subDistrictRef = useRef<HTMLInputElement>(null);
  const postalCodeRef = useRef<HTMLInputElement>(null);
  const bankRef = useRef<HTMLInputElement>(null);
  const occupationRef = useRef<HTMLInputElement>(null);
  const validateSelectionInputKeys = [
    "requirementsForMembershipRegistration",
    "birthdate",
    "birthMonth",
    "subDistrict",
    "bank",
    "occupation",
    "paCompany",
    "paPlan",
    "nonLifeInsuranceBrokerLicenseExpiredDate",
    "nonLifeInsuranceBrokerLicenseExpiredMonth",
    "nonLifeInsuranceBrokerLifeLicenseExpiredDate",
    "nonLifeInsuranceBrokerLifeLicenseExpiredMonth",
  ];
  const [searPostalCodeResults, setSearPostalCodeResults] = useState<
    SearchPostalCodeResult[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogSettings, setDialogSettings] = useState<
    Omit<TNotificationDialog, "isOpen" | "onClose">
  >({
    title: "",
    description: "",
    mode: DialogMode.SUCCESS,
    closeBtnTitle: "",
    confirmBtnTitle: "",
    onConfirmBtn: () => {},
  });
  const [isUploadingFile, setIsUploadingFile] = useState<
    Record<string, boolean>
  >({});
  const [birthdate, setBirthdate] = useState("");
  const [brokerExpiredDate, setBrokerExpiredDate] = useState("");
  const [brokerLifeExpiredDate, setBrokerLifeExpiredDate] = useState("");
  const [signatureImageFile, setSignatureImageFile] = useState<File>();
  // const recaptchaRef = useRef<ReCAPTCHA>(null);
  // const [verified, setVerified] = useState(false);

  useEffect(() => {
    tracker.start();

    return () => {
      tracker.stop();
    };
  }, []);

  useEffect(() => {
    if (isEmpty(searchZipcode)) {
      setSearPostalCodeResults([]);
      return;
    }
    const postalCodes = zipCodes
      .filter((e) => e.ZIPCODE.startsWith(searchZipcode))
      .slice(0, 20)
      .map((e) => {
        const subDistrict = subDistricts.find(
          (subDistrict) =>
            String(subDistrict.SUB_DISTRICT_ID) === e.SUB_DISTRICT_ID
        );
        return {
          postalCode: e.ZIPCODE,
          province:
            provinces.find(
              (province) => String(province.PROVINCE_ID) === e.PROVINCE_ID
            )?.PROVINCE_NAME ?? "",
          district:
            districts.find(
              (district) => String(district.DISTRICT_ID) === e.DISTRICT_ID
            )?.DISTRICT_NAME ?? "",
          subDistrictId: String(subDistrict?.SUB_DISTRICT_ID ?? ""),
          subDistrict: subDistrict?.SUB_DISTRICT_NAME ?? "",
        };
      });
    setSearPostalCodeResults(postalCodes);
  }, [searchZipcode]);

  const validateInputs = () => {
    const newValidInputs: ValidInputs = {
      uplinerId: true,
      requirementsForMembershipRegistration: !isEmpty(
        model?.requirementsForMembershipRegistration
      ),
      email: !isEmpty(model?.email),
      lineId: !isEmpty(model?.lineId),
      prefixForName: !isEmpty(model?.prefixForName),
      otherPrefixesForNames:
        model?.prefixForName !== "อื่น ๆ" ||
        !isEmpty(model?.otherPrefixesForNames),
      firstName: !isEmpty(model?.firstName),
      lastName: !isEmpty(model?.lastName),
      nationalIdNumber: !isEmpty(model?.nationalIdNumber),
      birthdate: true,
      birthMonth: true,
      birthYear: true,
      mobilePhone: !isEmpty(model?.mobilePhone),
      homePhone: true,
      workPhone: true,
      workPhoneExtensionNumber: true,
      maritalStatus: !isEmpty(model?.maritalStatus),
      prefixForSpouseName:
        model?.maritalStatus !== "สมรส" || !isEmpty(model?.prefixForSpouseName),
      spouseFirstName:
        model?.maritalStatus !== "สมรส" || !isEmpty(model?.spouseFirstName),
      spouseLastName:
        model?.maritalStatus !== "สมรส" || !isEmpty(model?.spouseLastName),
      nameOfTheLocation: true,
      roomOrFloor: true,
      addressNumber: !isEmpty(model?.addressNumber),
      villageNumber: true,
      soi: true,
      road: true,
      province: !isEmpty(model?.province),
      district: !isEmpty(model?.district),
      subDistrict: !isEmpty(model?.subDistrict),
      postalCode: !isEmpty(model?.postalCode),
      bankAccountType: !isEmpty(model?.bankAccountType),
      bank: model?.bankAccountType === "ไม่ระบุ" || !isEmpty(model?.bank),
      bankBranch:
        model?.bankAccountType === "ไม่ระบุ" || !isEmpty(model?.bankBranch),
      bankAccountName:
        model?.bankAccountType === "ไม่ระบุ" ||
        !isEmpty(model?.bankAccountName),
      bankAccountNumber:
        model?.bankAccountType === "ไม่ระบุ" ||
        !isEmpty(model?.bankAccountNumber),
      occupation: !isEmpty(model?.occupation),
      otherOccupations:
        model?.occupation !== "อื่น ๆ" || !isEmpty(model?.otherOccupations),
      paCompany: true,
      paPlan: true,
      hasANonLifeInsuranceBrokerLicense:
        model?.hasANonLifeInsuranceBrokerLicense !== undefined,
      nonLifeInsuranceBrokerLicenseNumber:
        !model.hasANonLifeInsuranceBrokerLicense ||
        !isEmpty(model?.nonLifeInsuranceBrokerLicenseNumber),
      nonLifeInsuranceBrokerLicenseExpiredDate: true,
      nonLifeInsuranceBrokerLicenseExpiredMonth: true,
      nonLifeInsuranceBrokerLicenseExpiredYear: true,
      hasANonLifeInsuranceBrokerLifeLicense:
        model?.hasANonLifeInsuranceBrokerLifeLicense !== undefined,
      nonLifeInsuranceBrokerLifeLicenseNumber:
        !model.hasANonLifeInsuranceBrokerLifeLicense ||
        !isEmpty(model?.nonLifeInsuranceBrokerLifeLicenseNumber),
      nonLifeInsuranceBrokerLifeLicenseExpiredDate: true,
      nonLifeInsuranceBrokerLifeLicenseExpiredMonth: true,
      nonLifeInsuranceBrokerLifeLicenseExpiredYear: true,
      beneficiaryFirstName: true,
      beneficiaryLastName: true,
      beneficiaryLastNationalIdNumber: true,
      nationalCardFileGDriveId: model.nationalCardFileGDriveId !== "",
      nationalCardFileGDriveIdPub: model.nationalCardFileGDriveIdPub !== "",
      bankAccountFileGDriveId: true,
      bankAccountFileGDriveIdPub: true,
      nonLifeInsuranceBrokerFileGDriveId: true,
      nonLifeInsuranceBrokerFileGDriveIdPub: true,
      nonLifeInsuranceBrokerLifeFileGDriveId: true,
      nonLifeInsuranceBrokerLifeFileGDriveIdPub: true,
      signatureImageUrl: true,
      paymentSlipGDriveId: model.paymentSlipGDriveId !== "",
      paymentSlipGDriveIdPub: model.paymentSlipGDriveIdPub !== "",
    };
    setValidInputs(newValidInputs);

    // Check for validation errors and create detailed error message
    const invalidFields = Object.keys(newValidInputs).filter(
      (key) => !newValidInputs[key as keyof MemberRegistrationRequest]
    );

    if (invalidFields.length > 0) {
      // Focus the first invalid input field
      const firstInvalidField = invalidFields[0];
      if (firstInvalidField) {
        focusInputField(firstInvalidField as keyof MemberRegistrationRequest);
      }

      // Create detailed error message
      const fieldLabels: Record<string, string> = {
        requirementsForMembershipRegistration: "ความต้องการในการสมัครสมาชิก",
        email: "อีเมล",
        lineId: "ไลน์ไอดี",
        prefixForName: "คำนำหน้าชื่อ",
        otherPrefixesForNames: "คำนำหน้าชื่อ อื่น ๆ",
        firstName: "ชื่อ",
        lastName: "นามสกุล",
        nationalIdNumber: "เลขบัตรประชาชน",
        mobilePhone: "โทรศัพท์มือถือ",
        maritalStatus: "สถานภาพ",
        prefixForSpouseName: "คำนำหน้าชื่อ (คู่สมรส)",
        spouseFirstName: "ชื่อ (คู่สมรส)",
        spouseLastName: "นามสกุล (คู่สมรส)",
        addressNumber: "เลขที่",
        province: "จังหวัด",
        district: "เขต/อำเภอ",
        subDistrict: "แขวง/ตำบล",
        postalCode: "รหัสไปรษณีย์",
        bankAccountType: "ประเภทบัญชี",
        bank: "ธนาคาร",
        bankBranch: "สาขา",
        bankAccountName: "ชื่อบัญชี",
        bankAccountNumber: "เลขที่บัญชี",
        occupation: "อาชีพ",
        otherOccupations: "อาชีพอื่น ๆ",
        hasANonLifeInsuranceBrokerLicense: "ใบอนุญาตนายหน้าประกันวินาศภัย",
        nonLifeInsuranceBrokerLicenseNumber: "เลขบัตรนายหน้าประกันวินาศภัย",
        hasANonLifeInsuranceBrokerLifeLicense: "ใบอนุญาตนายหน้าประกันชีวิต",
        nonLifeInsuranceBrokerLifeLicenseNumber: "เลขบัตรนายหน้าประกันชีวิต",
        nationalCardFileGDriveId: "สำเนาบัตรประชาชน",
        paymentSlipGDriveId: "หลักฐานการชำระเงิน",
      };

      const missingFields = invalidFields
        .map((field) => fieldLabels[field] || field)
        .join(", ");

      alert(
        `กรุณากรอกข้อมูลให้ครบถ้วน\n\nข้อมูลที่ขาดหายไป:\n${missingFields}`
      );
      return false;
    }
    return true;
  };

  const onSetModel = (key: keyof MemberRegistrationRequest, value: any) => {
    setModel((prev) => ({ ...prev, [key]: value }));
    if (validateSelectionInputKeys.some((e) => e === key)) {
      setValidInputs((prev) => ({ ...prev, [key]: true }));
    }
  };

  const dateToModelDate = (date: string) => {
    try {
      const [year, month, day] = date.split("-").map(Number);
      return { year, month, day };
    } catch {
      return { year: 0, month: 0, day: 0 };
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setIsLoading(true);

      // Reset validation state before checking
      setValidInputs({
        uplinerId: true,
        requirementsForMembershipRegistration: true,
        email: true,
        lineId: true,
        prefixForName: true,
        otherPrefixesForNames: true,
        firstName: true,
        lastName: true,
        nationalIdNumber: true,
        birthdate: true,
        birthMonth: true,
        birthYear: true,
        mobilePhone: true,
        homePhone: true,
        workPhone: true,
        workPhoneExtensionNumber: true,
        maritalStatus: true,
        prefixForSpouseName: true,
        spouseFirstName: true,
        spouseLastName: true,
        nameOfTheLocation: true,
        roomOrFloor: true,
        addressNumber: true,
        villageNumber: true,
        soi: true,
        road: true,
        province: true,
        district: true,
        subDistrict: true,
        postalCode: true,
        bankAccountType: true,
        bank: true,
        bankBranch: true,
        bankAccountName: true,
        bankAccountNumber: true,
        occupation: true,
        otherOccupations: true,
        paCompany: true,
        paPlan: true,
        hasANonLifeInsuranceBrokerLicense: true,
        nonLifeInsuranceBrokerLicenseNumber: true,
        nonLifeInsuranceBrokerLicenseExpiredDate: true,
        nonLifeInsuranceBrokerLicenseExpiredMonth: true,
        nonLifeInsuranceBrokerLicenseExpiredYear: true,
        hasANonLifeInsuranceBrokerLifeLicense: true,
        nonLifeInsuranceBrokerLifeLicenseNumber: true,
        nonLifeInsuranceBrokerLifeLicenseExpiredDate: true,
        nonLifeInsuranceBrokerLifeLicenseExpiredMonth: true,
        nonLifeInsuranceBrokerLifeLicenseExpiredYear: true,
        beneficiaryFirstName: true,
        beneficiaryLastName: true,
        beneficiaryLastNationalIdNumber: true,
        nationalCardFileGDriveId: true,
        nationalCardFileGDriveIdPub: true,
        bankAccountFileGDriveId: true,
        bankAccountFileGDriveIdPub: true,
        nonLifeInsuranceBrokerFileGDriveId: true,
        nonLifeInsuranceBrokerFileGDriveIdPub: true,
        nonLifeInsuranceBrokerLifeFileGDriveId: true,
        nonLifeInsuranceBrokerLifeFileGDriveIdPub: true,
        signatureImageUrl: true,
        paymentSlipGDriveId: true,
        paymentSlipGDriveIdPub: true,
      });

      tracker.setUserID(model.email);
      tracker.setMetadata("work", "register-member");

      const valid = validateInputs();
      if (!valid) {
        setIsLoading(false);
        return;
      }

      // const token = await recaptchaRef?.current?.executeAsync();
      // if (!token) {
      //   alert("Please verify captcha");
      //   return;
      // }
      // const recaptchaVerified = await recaptchaApi.verify(token);
      // if (!recaptchaVerified) {
      //   alert("CAPTCHA failed!");
      // }

      if (signatureImageFile === undefined) {
        setIsLoading(false);
        alert("กรุณาระบุลายเซ็นสำหรับใบสมัครสมาชิกศรีกรุง");
        return;
      }

      // Additional signature validation
      if (signatureImageFile.size === 0) {
        setIsLoading(false);
        openErrorDialog("กรุณาลงลายเซ็นใหม่");
        return;
      }

      let signatureImageUrl;
      try {
        const uploadResult = await uploadFile(signatureImageFile);
        signatureImageUrl = uploadResult.url;
      } catch (uploadError) {
        setIsLoading(false);
        openErrorDialog(
          uploadError instanceof Error
            ? uploadError.message
            : "ไม่สามารถอัปโหลดลายเซ็นได้ กรุณาลองใหม่อีกครั้ง"
        );
        return;
      }
      try {
        const uploadResult = await uploadFile(signatureImageFile);
        signatureImageUrl = uploadResult.url;
      } catch (uploadError) {
        setIsLoading(false);
        openErrorDialog(
          uploadError instanceof Error
            ? uploadError.message
            : "ไม่สามารถอัปโหลดลายเซ็นได้ กรุณาลองใหม่อีกครั้ง"
        );
        return;
      }

      const {
        year: birthYear,
        month: birthMonth,
        day: birthday,
      } = dateToModelDate(birthdate);

      const {
        year: nonLifeInsuranceBrokerLicenseExpiredYear,
        month: nonLifeInsuranceBrokerLicenseExpiredMonth,
        day: nonLifeInsuranceBrokerLicenseExpiredDate,
      } = dateToModelDate(brokerExpiredDate);

      const {
        year: nonLifeInsuranceBrokerLifeLicenseExpiredYear,
        month: nonLifeInsuranceBrokerLifeLicenseExpiredMonth,
        day: nonLifeInsuranceBrokerLifeLicenseExpiredDate,
      } = dateToModelDate(brokerLifeExpiredDate);

      const payload = {
        ...model,
        uplinerId: recommenderService.getId() ?? 1,
        birthYear,
        birthMonth,
        birthdate: birthday,
        nonLifeInsuranceBrokerLicenseExpiredYear,
        nonLifeInsuranceBrokerLicenseExpiredMonth,
        nonLifeInsuranceBrokerLicenseExpiredDate,
        nonLifeInsuranceBrokerLifeLicenseExpiredYear,
        nonLifeInsuranceBrokerLifeLicenseExpiredMonth,
        nonLifeInsuranceBrokerLifeLicenseExpiredDate,
        signatureImageUrl,
      };
      memberRegisterSchema.parse(payload);

      const { data, status } = await memberApi.create(payload);
      if (status === 200) {
        setDialogSettings({
          title: "สมัครสมาชิกสำเร็จ",
          description: `ระบบได้ทำการจัดทำไฟล์ข้อมูลการสมัครของท่าน \nกรุณาทำการกดปุ่มดาวน์โหลด์ไฟล์`,
          mode: DialogMode.SUCCESS,
          closeBtnTitle: "ปิด",
          confirmBtnTitle: "ดาวน์โหลดไฟล์",
          onCloseBtn: () => {
            setModel(defaultModel);
            setOpenDialog(false);
            setIsLoading(false);
          },
          onConfirmBtn: () => {
            window.open(data.data.file, "_blank")?.focus();
          },
          enabledPressBackdrop: false,
        });
        setOpenDialog(true);
      } else {
        openErrorDialog();
      }
    } catch (error) {
      setIsLoading(false);

      if (error instanceof ZodError) {
        const msg = error.errors.map((e) => e.message).join(", ");
        openErrorDialog(`ข้อมูลไม่ถูกต้อง: ${msg}`);
      } else if (error instanceof Error) {
        // Check if it's already handled upload error
        if (error.message.includes("อัปโหลดไฟล์ล้มเหลว")) {
          // Already handled above, don't show duplicate error
          return;
        }
        openErrorDialog(`เกิดข้อผิดพลาด: ${error.message}`);
      } else {
        openErrorDialog("เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง");
      }
    }
  };

  const openErrorDialog = (description = "กรุณาทำรายการใหม่อีกครั้ง") => {
    setDialogSettings({
      title: "มีบางอย่างผิดพลาด",
      description,
      mode: DialogMode.WARNING,
      confirmBtnTitle: "ตกลง",
      onConfirmBtn: () => {
        setOpenDialog(false);
      },
    });
    setOpenDialog(true);
  };

  const onCloseDialog = () => {
    setOpenDialog(false);
  };

  const focusInputField = (key: keyof MemberRegistrationRequest) => {
    switch (key) {
      case "requirementsForMembershipRegistration":
        requirementsForMembershipRegistrationRef.current?.autofocus;
        break;
      case "birthdate":
        birthdateRef.current?.focus({ preventScroll: false });
        break;
      case "birthMonth":
        birthMonthRef.current?.focus();
        break;
      case "birthYear":
        birthYearRef.current?.focus();
        break;
      case "province":
        provinceRef.current?.focus();
        break;
      case "district":
        districtRef.current?.focus();
        break;
      case "subDistrict":
        subDistrictRef.current?.focus();
        break;
      case "postalCode":
        postalCodeRef.current?.focus();
        break;
      case "bank":
        bankRef.current?.focus();
        break;
      case "occupation":
        occupationRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const onSelectPostalCode = ({
    postalCode,
    province,
    district,
    subDistrict,
  }: SearchPostalCodeResult) => {
    onSetModel("postalCode", postalCode);
    onSetModel("province", province);
    onSetModel("district", district);
    onSetModel("subDistrict", subDistrict);
    setSearchZipcode("");
    setSearPostalCodeResults([]);
  };

  const onInputFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files === null) return;
    return uploadFile(files[0]);
  };

  const uploadFile = async (file: File) => {
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);

      const formData = new FormData();
      formData.append("file", compressedFile);
      const { data } = await fileApi.create(formData);

      // Validate response structure
      if (!data || !data.data || !data.data.url) {
        throw new Error("ไม่สามารถอัปโหลดไฟล์ได้ กรุณาลองใหม่อีกครั้ง");
      }

      return data.data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`อัปโหลดไฟล์ล้มเหลว: ${error.message}`);
      }
      throw new Error("อัปโหลดไฟล์ล้มเหลว กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleNationalCardFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      if (!e) return;
      setIsUploadingFile({ handleNationalCardFileChange: true });
      const { id, idPub } = await onInputFile(e);
      onSetModel("nationalCardFileGDriveId", id);
      onSetModel("nationalCardFileGDriveIdPub", idPub);
    } catch (error) {
      openErrorDialog("ไม่สามารถอัปโหลดสำเนาบัตรประชาชนได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsUploadingFile({ handleNationalCardFileChange: false });
    }
  };

  const handleBankAccountFileGDriveIdFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      if (!e) return;
      setIsUploadingFile({ handleBankAccountFileGDriveIdFileChange: true });
      const { id, idPub } = await onInputFile(e);
      onSetModel("bankAccountFileGDriveId", id);
      onSetModel("bankAccountFileGDriveIdPub", idPub);
    } catch (error) {
      openErrorDialog("ไม่สามารถอัปโหลดหน้าสมุดบัญชีได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsUploadingFile({ handleBankAccountFileGDriveIdFileChange: false });
    }
  };

  const handleNonLifeInsuranceBrokerFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      if (!e) return;
      setIsUploadingFile({ handleNonLifeInsuranceBrokerFileChange: true });
      const { id, idPub } = await onInputFile(e);
      onSetModel("nonLifeInsuranceBrokerFileGDriveId", id);
      onSetModel("nonLifeInsuranceBrokerFileGDriveIdPub", idPub);
    } catch (error) {
      openErrorDialog("ไม่สามารถอัปโหลดบัตรนายหน้าประกันวินาศภัยได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsUploadingFile({ handleNonLifeInsuranceBrokerFileChange: false });
    }
  };

  const handleNonLifeInsuranceBrokerLifeFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      if (!e) return;
      setIsUploadingFile({ handleNonLifeInsuranceBrokerLifeFileChange: true });
      const { id, idPub } = await onInputFile(e);
      onSetModel("nonLifeInsuranceBrokerLifeFileGDriveId", id);
      onSetModel("nonLifeInsuranceBrokerLifeFileGDriveIdPub", idPub);
    } catch (error) {
      openErrorDialog("ไม่สามารถอัปโหลดบัตรนายหน้าประกันชีวิตได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsUploadingFile({ handleNonLifeInsuranceBrokerLifeFileChange: false });
    }
  };

  const handlePaymentSlipFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      if (!e) return;
      setIsUploadingFile({ handlePaymentSlipFileChange: true });
      const { id, idPub } = await onInputFile(e);
      onSetModel("paymentSlipGDriveId", id);
      onSetModel("paymentSlipGDriveIdPub", idPub);
    } catch (error) {
      openErrorDialog("ไม่สามารถอัปโหลดหลักฐานการชำระเงินได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsUploadingFile({ handlePaymentSlipFileChange: false });
    }
  };

  const handleDeleteNationalCardFile = () => {
    onSetModel("nationalCardFileGDriveId", "");
    onSetModel("nationalCardFileGDriveIdPub", "");
  };

  const handleDeleteBankAccountFile = () => {
    onSetModel("bankAccountFileGDriveId", "");
    onSetModel("bankAccountFileGDriveIdPub", "");
  };

  const handleDeleteNonLifeInsuranceBrokerFile = () => {
    onSetModel("nonLifeInsuranceBrokerFileGDriveId", "");
    onSetModel("nonLifeInsuranceBrokerFileGDriveIdPub", "");
  };

  const handleDeleteNonLifeInsuranceBrokerLifeFile = () => {
    onSetModel("nonLifeInsuranceBrokerLifeFileGDriveId", "");
    onSetModel("nonLifeInsuranceBrokerLifeFileGDriveIdPub", "");
  };

  const handleDeletePaymentSlipFile = () => {
    onSetModel("paymentSlipGDriveId", "");
    onSetModel("paymentSlipGDriveIdPub", "");
  };

  return (
    <section className="px-8 py-20 container mx-auto">
      <title>สมัครสมาชิก | Agent Srikrung</title>
      <NotificationDialog
        mode={dialogSettings.mode}
        isOpen={openDialog}
        onClose={onCloseDialog}
        title={dialogSettings.title}
        description={dialogSettings.description}
        closeBtnTitle={dialogSettings.closeBtnTitle}
        onCloseBtn={dialogSettings.onCloseBtn}
        confirmBtnTitle={dialogSettings.confirmBtnTitle}
        onConfirmBtn={dialogSettings.onConfirmBtn}
        enabledPressBackdrop={dialogSettings.enabledPressBackdrop}
      />
      <RecommenderCard hasButton={false} />
      <RegisterMemberInfo />
      <Typography variant="h4" color="blue-gray" className="font-noto">
        แบบฟอร์มสมัคร สมาชิกศรีกรุงโบรคเกอร์
      </Typography>
      <form className="flex flex-col mt-8" onSubmit={onSubmit} action="">
        <hr />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          ข้อมูลส่วนตัว (ผู้สมัคร)
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ความต้องการในการสมัครสมาชิก
            </Typography>
            <Select
              size="lg"
              label="เลือกความต้องการ"
              value={model.requirementsForMembershipRegistration}
              onChange={(v) =>
                onSetModel("requirementsForMembershipRegistration", v)
              }
              error={!validInputs?.requirementsForMembershipRegistration}
              ref={requirementsForMembershipRegistrationRef}
              disabled={isLoading}
            >
              {requirements.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              อีเมล
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              type="email"
              required
              value={model.email}
              onChange={(e) => onSetModel("email", e.target.value)}
              error={!validInputs?.email}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ไลน์ไอดี
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              required
              value={model.lineId}
              onChange={(e) => onSetModel("lineId", e.target.value)}
              error={!validInputs?.lineId}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="mb-6 flex flex-col items-end gap-4 md:flex-row">
          <div className=" w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              คำนำหน้าชื่อ
            </Typography>
            <div className="gap-1">
              {namePrefix.map((prefix) => (
                <Radio
                  key={prefix}
                  name={prefix}
                  label={prefix}
                  value={prefix}
                  checked={prefix === model.prefixForName}
                  onChange={(e) => onSetModel("prefixForName", e.target.value)}
                  className="w-5 h-5"
                  required={model.prefixForName === undefined}
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>
          {model.prefixForName === "อื่น ๆ" && (
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                คำนำหน้าชื่อ อื่น ๆ
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                required
                value={model.otherPrefixesForNames}
                onChange={(e) =>
                  onSetModel("otherPrefixesForNames", e.target.value)
                }
                error={!validInputs?.otherPrefixesForNames}
                disabled={isLoading}
              />
            </div>
          )}
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ชื่อ (ผู้สมัคร)
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              required
              value={model.firstName}
              onChange={(e) => onSetModel("firstName", e.target.value)}
              error={!validInputs?.firstName}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              นามสกุล (ผู้สมัคร)
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              required
              value={model.lastName}
              onChange={(e) => onSetModel("lastName", e.target.value)}
              error={!validInputs?.lastName}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              เลขบัตรประชาชน
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              required
              value={model.nationalIdNumber}
              onChange={(e) => onSetModel("nationalIdNumber", e.target.value)}
              error={!validInputs?.nationalIdNumber}
              disabled={isLoading}
              minLength={13}
              maxLength={13}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              โทรศัพท์มือถือ
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              required
              value={model.mobilePhone}
              onChange={(e) => onSetModel("mobilePhone", e.target.value)}
              error={!validInputs?.mobilePhone}
              disabled={isLoading}
              minLength={10}
              maxLength={10}
            />
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              วัน/เดือน/ปี (เกิด)
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              required
              type="date"
              value={birthdate}
              onChange={(e) => {
                setBirthdate(e.target.value);
              }}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              โทรศัพท์ที่บ้าน
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.homePhone}
              onChange={(e) => onSetModel("homePhone", e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="mb-6 flex flex-col items-end gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              โทรศัพท์ที่ทำงาน
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.workPhone}
              onChange={(e) => onSetModel("workPhone", e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              เบอร์ต่อภายใน
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.workPhoneExtensionNumber}
              onChange={(e) =>
                onSetModel("workPhoneExtensionNumber", e.target.value)
              }
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="mb-6 flex flex-col items-end gap-4 md:flex-row">
          <div className=" w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              สถานภาพ
            </Typography>
            <div className="gap-1">
              {maritalStatuses.map((status) => (
                <Radio
                  key={status}
                  name={status}
                  value={status}
                  label={status}
                  checked={status === model.maritalStatus}
                  onChange={(e) => onSetModel("maritalStatus", e.target.value)}
                  className="w-5 h-5"
                  required={model.maritalStatus === undefined}
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>
          {model.maritalStatus === "สมรส" && (
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                คำนำหน้าชื่อ (คู่สมรส)
              </Typography>
              <div className="gap-1">
                {prefixForSpouseNames.map((prefix) => (
                  <Radio
                    key={prefix}
                    name={prefix}
                    value={prefix}
                    label={prefix}
                    checked={prefix === model.prefixForSpouseName}
                    onChange={(e) =>
                      onSetModel("prefixForSpouseName", e.target.value)
                    }
                    className="w-5 h-5"
                    required={model.prefixForSpouseName === undefined}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {model.maritalStatus === "สมรส" && (
          <div className="mb-6 flex flex-col items-end gap-4 md:flex-row">
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                ชื่อ (คู่สมรส)
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                required
                value={model.spouseFirstName}
                onChange={(e) => onSetModel("spouseFirstName", e.target.value)}
                error={!validInputs?.spouseFirstName}
                disabled={isLoading}
              />
            </div>
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                นามสกุล (คู่สมรส)
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                required
                value={model.spouseLastName}
                onChange={(e) => onSetModel("spouseLastName", e.target.value)}
                error={!validInputs?.spouseLastName}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          ที่อยู่ปัจจุบันที่สามารถติดต่อได้
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ชื่อสถานที่ อาคาร / บริษัท / หมู่บ้าน
            </Typography>
            <Input
              size="lg"
              label=""
              value={model.nameOfTheLocation}
              onChange={(e) => onSetModel("nameOfTheLocation", e.target.value)}
              error={!validInputs?.nameOfTheLocation}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ห้อง / ชั้น
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.roomOrFloor}
              onChange={(e) => onSetModel("roomOrFloor", e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              เลขที่
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              value={model.addressNumber}
              onChange={(e) => onSetModel("addressNumber", e.target.value)}
              error={!validInputs?.addressNumber}
              disabled={isLoading}
              required
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              หมู่ที่
            </Typography>
            <Input
              size="lg"
              label=""
              value={model.villageNumber}
              onChange={(e) => onSetModel("villageNumber", e.target.value)}
              error={!validInputs?.villageNumber}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ซอย
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.soi}
              onChange={(e) => onSetModel("soi", e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ถนน
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.road}
              onChange={(e) => onSetModel("road", e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ค้นหาด้วยรหัสไปรษณีย์
            </Typography>
            <Input
              size="lg"
              label="กรอกรหัสไปรษณีย์"
              value={searchZipcode}
              onChange={(e) => setSearchZipcode(e.target.value)}
              maxLength={5}
              disabled={isLoading}
            />
            {searPostalCodeResults.length > 0 && (
              <div className="relative flex flex-col rounded-lg bg-white shadow-sm border border-slate-200">
                <nav className="min-w-[240px] w-full max-h-60 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-xl [&::-webkit-scrollbar-thumb]:bg-[#b8b4b4] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-xl [&::-webkit-scrollbar-track]:bg-slate-100">
                  {searPostalCodeResults.map((item, index) => (
                    <Button
                      key={item.subDistrictId}
                      variant="text"
                      className="text-start flex items-center gap-1 w-full"
                      onClick={() => onSelectPostalCode(item)}
                      name={`searPostalCodeResults_${index}`}
                    >
                      {item.postalCode} <ArrowIcon /> {item.province}{" "}
                      <ArrowIcon /> {item.district} <ArrowIcon />{" "}
                      {item.subDistrict}
                    </Button>
                  ))}
                </nav>
              </div>
            )}
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            {/* <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              จังหวัด
            </Typography>
            <Input
              label="กรอกข้อมูล"
              value={model.province}
              disabled={isLoading}
              required
              onChange={(e) => onSetModel("province", e.target.value)}
            /> */}
            <ProvinceSelect
              provinces={provinces}
              value={model.province}
              onChange={(value: string) => onSetModel("province", value)}
            />
          </div>

          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              เขต/อำเภอ
            </Typography>
            <Input
              label="กรอกข้อมูล"
              value={model.district}
              disabled={isLoading}
              required
              onChange={(e) => onSetModel("district", e.target.value)}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              แขวง/ตำบล
            </Typography>
            <Input
              label="กรอกข้อมูล"
              value={model.subDistrict}
              disabled={isLoading}
              required
              onChange={(e) => onSetModel("subDistrict", e.target.value)}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              รหัสไปรษณีย์
            </Typography>
            <Input
              label="กรอกข้อมูล"
              value={model.postalCode}
              disabled={isLoading}
              required
              onChange={(e) => onSetModel("postalCode", e.target.value)}
              minLength={5}
              maxLength={5}
            />
          </div>
        </div>

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          ข้อมูลธนาคาร
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ประเภทบัญชี
            </Typography>
            <div className="gap-1">
              {bankAccountTypes.map((bank) => (
                <Radio
                  key={bank}
                  name={bank}
                  value={bank}
                  label={bank}
                  checked={bank === model.bankAccountType}
                  onChange={(e) =>
                    onSetModel("bankAccountType", e.target.value)
                  }
                  className="w-5 h-5"
                  required={model.bankAccountType === undefined}
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>
        </div>
        {model.bankAccountType !== "ไม่ระบุ" &&
          model.bankAccountType !== "" && (
            <>
              <div className="mb-6 flex flex-col gap-4 md:flex-row">
                <div className="w-full">
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-2 font-medium font-noto"
                  >
                    ธนาคาร
                  </Typography>
                  <Select
                    size="lg"
                    label="เลือกธนาคาร"
                    ref={bankRef}
                    error={!validInputs["bank"]}
                    value={model.bank}
                    onChange={(v) => onSetModel("bank", v)}
                    disabled={isLoading}
                  >
                    {banks.map((option) => (
                      <Option key={option} value={option}>
                        {option}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="w-full">
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-2 font-medium font-noto"
                  >
                    สาขา
                  </Typography>
                  <Input
                    size="lg"
                    label="กรอกข้อมูล"
                    required
                    value={model.bankBranch}
                    onChange={(e) => onSetModel("bankBranch", e.target.value)}
                    error={!validInputs?.bankBranch}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="mb-6 flex flex-col gap-4 md:flex-row">
                <div className="w-full">
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-2 font-medium font-noto"
                  >
                    ชื่อบัญชี
                  </Typography>
                  <Input
                    size="lg"
                    label="กรอกข้อมูล"
                    required
                    value={model.bankAccountName}
                    onChange={(e) =>
                      onSetModel("bankAccountName", e.target.value)
                    }
                    error={!validInputs?.bankAccountName}
                    disabled={isLoading}
                  />
                </div>
                <div className="w-full">
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-2 font-medium font-noto"
                  >
                    เลขที่บัญชี
                  </Typography>
                  <Input
                    size="lg"
                    label="กรอกข้อมูล"
                    required
                    value={model.bankAccountNumber}
                    onChange={(e) =>
                      onSetModel("bankAccountNumber", e.target.value)
                    }
                    error={!validInputs?.bankAccountNumber}
                    disabled={isLoading}
                    maxLength={10}
                    minLength={10}
                  />
                </div>
              </div>
            </>
          )}

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          ปัจจุบันประกอบอาชีพ
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              อาชีพ
            </Typography>
            <Select
              size="lg"
              label="เลือกอาชีพ"
              ref={occupationRef}
              error={!validInputs["occupation"]}
              value={model.occupation}
              onChange={(v) => onSetModel("occupation", v)}
              disabled={isLoading}
            >
              {occupations.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </div>
          {model.occupation === "อื่น ๆ" && (
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                อาชีพอื่น ๆ
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                required
                value={model.otherOccupations}
                onChange={(e) => onSetModel("otherOccupations", e.target.value)}
                error={!validInputs?.otherOccupations}
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          แพ็กเกจ PA
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              บริษัทประกัน (ไม่จำเป็นต้องระบุ)
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              value={model.paCompany}
              onChange={(e) => onSetModel("paCompany", e.target.value)}
              error={!validInputs?.paCompany}
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              แผน
            </Typography>
            <Input
              size="lg"
              label="กรอกข้อมูล"
              value={model.paPlan}
              onChange={(e) => onSetModel("paPlan", e.target.value)}
              error={!validInputs?.paPlan}
              disabled={isLoading}
            />
          </div>
        </div>

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          บัตรอนุญาตนายหน้า
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ใบอนุญาตนายหน้าประกันวินาศภัย
            </Typography>
            <div className="gap-1">
              <Radio
                key="hasANonLifeInsuranceBrokerLicense-no"
                name="hasANonLifeInsuranceBrokerLicense-no"
                label="ไม่มี"
                checked={!model.hasANonLifeInsuranceBrokerLicense}
                onChange={() =>
                  onSetModel("hasANonLifeInsuranceBrokerLicense", false)
                }
                className="w-5 h-5"
                required={model.hasANonLifeInsuranceBrokerLicense === undefined}
                disabled={isLoading}
              />
              <Radio
                key="hasANonLifeInsuranceBrokerLicense-yes"
                name="hasANonLifeInsuranceBrokerLicense-yes"
                label="มี"
                checked={model.hasANonLifeInsuranceBrokerLicense}
                onChange={() =>
                  onSetModel("hasANonLifeInsuranceBrokerLicense", true)
                }
                className="w-5 h-5"
                required={model.hasANonLifeInsuranceBrokerLicense === undefined}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {model.hasANonLifeInsuranceBrokerLicense && (
          <div className="mb-6 flex flex-col gap-4 md:flex-row">
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                เลขบัตรนายหน้า
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                required
                value={model.nonLifeInsuranceBrokerLicenseNumber ?? ""}
                onChange={(e) =>
                  onSetModel(
                    "nonLifeInsuranceBrokerLicenseNumber",
                    e.target.value
                  )
                }
                error={!validInputs?.nonLifeInsuranceBrokerLicenseNumber}
                disabled={isLoading}
              />
            </div>
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                วัน/เดือน/ปี (หมดอายุ)
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                type="date"
                required
                value={brokerExpiredDate}
                onChange={(e) => setBrokerExpiredDate(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ใบอนุญาตนายหน้าประกันชีวิต
            </Typography>
            <div className="gap-1">
              <Radio
                key="hasANonLifeInsuranceBrokerLifeLicense-no"
                name="hasANonLifeInsuranceBrokerLifeLicense-no"
                label="ไม่มี"
                checked={!model.hasANonLifeInsuranceBrokerLifeLicense}
                onChange={() =>
                  onSetModel("hasANonLifeInsuranceBrokerLifeLicense", false)
                }
                className="w-5 h-5"
                required={
                  model.hasANonLifeInsuranceBrokerLifeLicense === undefined
                }
                disabled={isLoading}
              />
              <Radio
                key="hasANonLifeInsuranceBrokerLifeLicense-yes"
                name="hasANonLifeInsuranceBrokerLifeLicense-yes"
                label="มี"
                checked={model.hasANonLifeInsuranceBrokerLifeLicense}
                onChange={() =>
                  onSetModel("hasANonLifeInsuranceBrokerLifeLicense", true)
                }
                className="w-5 h-5"
                required={
                  model.hasANonLifeInsuranceBrokerLifeLicense === undefined
                }
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
        {model.hasANonLifeInsuranceBrokerLifeLicense && (
          <div className="mb-6 flex flex-col gap-4 md:flex-row">
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                เลขบัตรนายหน้า
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                required
                value={model.nonLifeInsuranceBrokerLifeLicenseNumber ?? ""}
                onChange={(e) =>
                  onSetModel(
                    "nonLifeInsuranceBrokerLifeLicenseNumber",
                    e.target.value
                  )
                }
                error={!validInputs?.nonLifeInsuranceBrokerLifeLicenseNumber}
                disabled={isLoading}
              />
            </div>
            <div className="w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium font-noto"
              >
                วัน/เดือน/ปี (หมดอายุ)
              </Typography>
              <Input
                size="lg"
                label="กรอกข้อมูล"
                type="date"
                required
                value={brokerLifeExpiredDate}
                onChange={(e) => setBrokerLifeExpiredDate(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          ชื่อผู้รับผลประโยชน์ (กรณีไม่ระบุ ผู้รับผลประโยชน์คือ ทายาทโดยธรรม)
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              ชื่อ
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.beneficiaryFirstName}
              onChange={(e) =>
                onSetModel("beneficiaryFirstName", e.target.value)
              }
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              นามสกุล
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.beneficiaryLastName}
              onChange={(e) =>
                onSetModel("beneficiaryLastName", e.target.value)
              }
              disabled={isLoading}
            />
          </div>
          <div className="w-full">
            <Typography
              variant="small"
              color="blue-gray"
              className="mb-2 font-medium font-noto"
            >
              เลขบัตรประชาชน
            </Typography>
            <Input
              size="lg"
              label=""
              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              value={model.beneficiaryLastNationalIdNumber}
              onChange={(e) =>
                onSetModel("beneficiaryLastNationalIdNumber", e.target.value)
              }
              disabled={isLoading}
              minLength={13}
              maxLength={13}
            />
          </div>
        </div>

        <hr className="mt-5" />
        <Typography variant="h5" className="font-normal mb-5 mt-5 font-noto">
          เอกสาร
        </Typography>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <div className="flex flex-row items-center gap-3 mb-3">
              <Typography
                variant="small"
                color="blue-gray"
                className="font-medium font-noto text-center"
              >
                สำเนาบัตรประชาชน
              </Typography>
              {model.nationalCardFileGDriveId && (
                <UploadedFile
                  title="สำเนาบัตรประชาชน"
                  url={`${gDriveUrl}/${model.nationalCardFileGDriveId}`}
                  onDelete={handleDeleteNationalCardFile}
                />
              )}
            </div>
            <Input
              size="lg"
              label="รองรับไฟล์ประเภท .pdf .png .jpg .jpeg"
              type="file"
              accept=".pdf,image/*"
              required
              onChange={handleNationalCardFileChange}
              disabled={
                isLoading || isUploadingFile["handleNationalCardFileChange"]
              }
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <div className="flex flex-row items-center gap-3 mb-3">
              <Typography
                variant="small"
                color="blue-gray"
                className="font-medium font-noto text-center"
              >
                หลักฐานการชำระเงิน
              </Typography>
              {model.paymentSlipGDriveId && (
                <UploadedFile
                  title="หลักฐานการชำระเงิน"
                  url={`${gDriveUrl}/${model.paymentSlipGDriveId}`}
                  onDelete={handleDeletePaymentSlipFile}
                />
              )}
            </div>
            <Input
              size="lg"
              label="รองรับไฟล์ประเภท .pdf .png .jpg .jpeg"
              type="file"
              accept=".pdf,image/*"
              onChange={handlePaymentSlipFileChange}
              disabled={
                isLoading || isUploadingFile["handlePaymentSlipFileChange"]
              }
              required
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <div className="flex flex-row items-center gap-3 mb-3">
              <Typography
                variant="small"
                color="blue-gray"
                className="font-medium font-noto text-center"
              >
                หน้าสมุดบัญชี (ถ้ามี)
              </Typography>
              {model.bankAccountFileGDriveId && (
                <UploadedFile
                  title="หน้าสมุดบัญชี"
                  url={`${gDriveUrl}/${model.bankAccountFileGDriveId}`}
                  onDelete={handleDeleteBankAccountFile}
                />
              )}
            </div>
            <Input
              size="lg"
              label="รองรับไฟล์ประเภท .pdf .png .jpg .jpeg"
              type="file"
              accept=".pdf,image/*"
              onChange={handleBankAccountFileGDriveIdFileChange}
              disabled={
                isLoading ||
                isUploadingFile["handleBankAccountFileGDriveIdFileChange"]
              }
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <div className="flex flex-row items-center gap-3 mb-3">
              <Typography
                variant="small"
                color="blue-gray"
                className="font-medium font-noto text-center"
              >
                บัตรนายหน้าประกันวินาศภัย (ถ้ามี)
              </Typography>
              {model.nonLifeInsuranceBrokerFileGDriveId && (
                <UploadedFile
                  title="บัตรนายหน้าประกันวินาศภัย"
                  url={`${gDriveUrl}/${model.nonLifeInsuranceBrokerFileGDriveId}`}
                  onDelete={handleDeleteNonLifeInsuranceBrokerFile}
                />
              )}
            </div>
            <Input
              size="lg"
              label="รองรับไฟล์ประเภท .pdf .png .jpg .jpeg"
              type="file"
              accept=".pdf,image/*"
              onChange={handleNonLifeInsuranceBrokerFileChange}
              disabled={
                isLoading ||
                isUploadingFile["handleNonLifeInsuranceBrokerFileChange"]
              }
            />
          </div>
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="w-full">
            <div className="flex flex-row items-center gap-3 mb-3">
              <Typography
                variant="small"
                color="blue-gray"
                className="font-medium font-noto text-center"
              >
                บัตรนายหน้าประกันชีวิต (ถ้ามี)
              </Typography>
              {model.nonLifeInsuranceBrokerLifeFileGDriveId && (
                <UploadedFile
                  title="บัตรนายหน้าประกันชีวิต"
                  url={`${gDriveUrl}/${model.nonLifeInsuranceBrokerLifeFileGDriveId}`}
                  onDelete={handleDeleteNonLifeInsuranceBrokerLifeFile}
                />
              )}
            </div>
            <Input
              size="lg"
              label="รองรับไฟล์ประเภท .pdf .png .jpg .jpeg"
              type="file"
              accept=".pdf,image/*"
              onChange={handleNonLifeInsuranceBrokerLifeFileChange}
              disabled={
                isLoading ||
                isUploadingFile["handleNonLifeInsuranceBrokerLifeFileChange"]
              }
            />
          </div>
        </div>
        <Sign
          signatureImageFile={signatureImageFile}
          setSignatureImageFile={setSignatureImageFile}
        />

        {/* <ReCAPTCHA
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
          ref={recaptchaRef}
          onChange={() => setVerified(true)}
          className="mt-10"
        /> */}
        <Button
          className="mt-12 bg-[#188F46] font-noto"
          fullWidth
          type="submit"
          disabled={isLoading}
          name="submit"
        >
          {isLoading ? "กำลังส่งข้อมูลการสมัคร..." : "ส่งข้อมูลการสมัคร"}
        </Button>
      </form>
    </section>
  );
};

export default MemberRegister;
